/**
 * Tournament Match Generation Utilities
 * Generates round robin matches for group stages
 */

/**
 * Generate round robin pairings for a group of teams
 * Each team plays every other team exactly once
 * 
 * @param {Array<string>} teamIds - Array of team IDs
 * @returns {Array<{team1Id: string, team2Id: string}>} Array of match pairings
 * 
 * @example
 * // For 4 teams: A, B, C, D
 * // Returns: A-B, A-C, A-D, B-C, B-D, C-D (6 matches)
 * generateRoundRobinMatches(['A', 'B', 'C', 'D'])
 */
export const generateRoundRobinMatches = (teamIds) => {
  if (!Array.isArray(teamIds) || teamIds.length < 2) {
    throw new Error('At least 2 teams are required for round robin');
  }

  const matches = [];
  
  // Generate all unique pairings
  for (let i = 0; i < teamIds.length; i++) {
    for (let j = i + 1; j < teamIds.length; j++) {
      matches.push({
        team1Id: teamIds[i],
        team2Id: teamIds[j]
      });
    }
  }
  
  return matches;
};

/**
 * Calculate total number of matches in round robin
 * Formula: n * (n - 1) / 2
 * 
 * @param {number} teamCount - Number of teams
 * @returns {number} Total matches
 */
export const calculateRoundRobinMatchCount = (teamCount) => {
  if (teamCount < 2) return 0;
  return (teamCount * (teamCount - 1)) / 2;
};

/**
 * Generate round robin matches for all groups
 * 
 * @param {Array<{groupId: string, teamIds: Array<string>}>} groupsWithTeams - Groups with their team IDs
 * @returns {Array<{groupId: string, team1Id: string, team2Id: string}>} All group matches
 */
export const generateGroupStageMatches = (groupsWithTeams) => {
  const allMatches = [];
  
  for (const group of groupsWithTeams) {
    const groupMatches = generateRoundRobinMatches(group.teamIds);
    
    // Add group ID to each match
    for (const match of groupMatches) {
      allMatches.push({
        groupId: group.groupId,
        team1Id: match.team1Id,
        team2Id: match.team2Id
      });
    }
  }
  
  return allMatches;
};

/**
 * Validate group configuration for auto-generation.
 * Teams need not divide evenly; each group must have at least 2 teams (round robin).
 *
 * @param {number} totalTeams - Total number of teams
 * @param {number} groupCount - Number of groups
 * @returns {{valid: boolean, error?: string}} Validation result
 */
export const validateGroupConfiguration = (totalTeams, groupCount) => {
  if (totalTeams < 2) {
    return { valid: false, error: 'At least 2 teams are required' };
  }

  if (groupCount < 1) {
    return { valid: false, error: 'At least 1 group is required' };
  }

  if (totalTeams < groupCount) {
    return { valid: false, error: `Cannot use ${groupCount} groups with only ${totalTeams} team(s)` };
  }

  const minPerGroup = Math.floor(totalTeams / groupCount);
  if (minPerGroup < 2) {
    return {
      valid: false,
      error: `Need at least ${groupCount * 2} teams for ${groupCount} groups (minimum 2 per group). You have ${totalTeams}.`,
    };
  }

  return { valid: true };
};

/**
 * Auto-distribute teams into groups (balanced).
 * Round-robin assignment: group sizes differ by at most 1 (as even as possible).
 *
 * @param {Array<string>} teamIds - All team IDs
 * @param {number} groupCount - Number of groups
 * @returns {Array<{groupIndex: number, teamIds: Array<string>}>} Teams distributed into groups
 */
export const distributeTeamsIntoGroups = (teamIds, groupCount) => {
  if (groupCount < 1) {
    throw new Error('At least 1 group is required');
  }

  const groups = Array.from({ length: groupCount }, (_, i) => ({
    groupIndex: i,
    teamIds: [],
  }));

  teamIds.forEach((teamId, index) => {
    const groupIndex = index % groupCount;
    groups[groupIndex].teamIds.push(teamId);
  });

  return groups;
};

/**
 * Random balanced distribution: shuffle team order, then same even spread as {@link distributeTeamsIntoGroups}.
 * Group sizes still differ by at most one (valid for round robin).
 */
export const distributeTeamsIntoGroupsRandom = (teamIds, groupCount) => {
  const shuffled = [...teamIds];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return distributeTeamsIntoGroups(shuffled, groupCount);
};

/**
 * Generate group names (A, B, C, D, ...)
 * 
 * @param {number} groupCount - Number of groups
 * @returns {Array<string>} Group names
 */
export const generateGroupNames = (groupCount) => {
  const names = [];
  for (let i = 0; i < groupCount; i++) {
    names.push(String.fromCharCode(65 + i)); // 65 = 'A'
  }
  return names;
};
